import { Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { Badges, Btn, H3, H4, H5, H6, LI, P, SVG, UL } from "../../AbstractElements";
import { URL_API } from "../../utils/helper/helpers";
import SweetAlert from 'sweetalert2';
import Breadcrumbs from "../../CommonElements/Breadcrumbs/Breadcrumbs";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import '../../utils/helper/style.css';

import { Link } from "react-router-dom";
import { formatDate, formatPrice, itemHasPermission } from "../../utils/helper/helpers";
import Chart, { deleteVersement } from "./apiVersement";
import useInfosEts from "../InfosEts/InfoApi";

// Types
type Verse = { [key: string]: any };

interface VersementAggregated {
    qts: number;
    qtt: number;
    mt: number;
    tqts: number;
    tqtt: number;
    tmt: number;
    name: string;
}

interface VersementsData {
    success: boolean;
    diamond: VersementAggregated;
    diamond_sougueta: VersementAggregated;
    cimaf: VersementAggregated;
    ciment_de_guinee: VersementAggregated;
    versements: Verse[];
    [key: string]: VersementAggregated | Verse[] | boolean; // Signature d'index ajoutée
}

const GestVersementsV2 = () => {

    const Token = JSON.parse(localStorage.getItem("login")!);
    const [filterText, setFilterText] = useState("");
    const [filteredItems, setFilteredItems] = useState<Verse[]>([]);
    const [data, setData] = useState<VersementsData | null>(null);
    const [refresh, setRefresh] = useState(false);
    const [chartData, setChartData] = useState<number[]>([0, 0, 0]);
    const [monthFilter, setMonthFilter] = useState<string>("");
    const [etsFilter, setEtsFilter] = useState<string>("");
    const infosEts = useInfosEts(refresh) || [];

    // Récupération initiale des filtres sauvegardés
    useEffect(() => {
        const savedFilterText = localStorage.getItem("filterText_versement") || "";
        const savedSelectedMonth = localStorage.getItem("selectedMonth_versement");
        const savedEtsFilter = localStorage.getItem("etsFilter_versement") || "";

        setFilterText(savedFilterText);
        setMonthFilter(savedSelectedMonth ? savedSelectedMonth : '');
        setEtsFilter(savedEtsFilter);
    }, []);

    // Sauvegarde des filtres dans localStorage à chaque changement
    useEffect(() => {
        localStorage.setItem("filterText_versement", filterText);
        localStorage.setItem("selectedMonth_versement", monthFilter ? monthFilter.toString() : "");
        localStorage.setItem("etsFilter_versement", etsFilter);
    }, [filterText, monthFilter, etsFilter]);

    // Bouton pour réinitialiser les filtres
    const clearFilters = () => {
        localStorage.removeItem("filterText_versement");
        localStorage.removeItem("selectedMonth_versement");
        localStorage.removeItem("etsFilter_versement");

        setFilterText("");
        setMonthFilter("");
        setEtsFilter("");
    };

    const filterByMonth = (versements: Verse[], month: string) => {
        if (!month) return versements;

        return versements.filter((item) => {
            // Vérifier que la date existe
            if (item.pv_date_paiement) {
                const date = new Date(item.pv_date_paiement);  // Log pour vérifier la date et le mois

                return date.getMonth() + 1 === parseInt(month); // Comparer le mois
            }
            return false; // Si pv_date_paiement est manquante ou invalide, on l'ignore
        });
    };

    const filterByEts = (versements: Verse[], code: string) => {
        if (!code) return versements;

        const transformCode = code.toLocaleLowerCase()
        return versements.filter((item) => (item.ets_code && item.ets_code.toLowerCase().includes(transformCode)))
    };

    // Fetch data from API
    const fetchVersements = useCallback(async () => {
        try {
            const response = await axios.get(`${URL_API}/versement/all`, {
                headers: { Authorization: `Bearer ${Token}` }
            });
            if (response.data.success) {
                setData(response.data);
                setChartData([
                    response.data.diamond.qtt ?? 0,
                    response.data.diamond_sougueta.qtt ?? 0,
                    response.data.cimaf.qtt ?? 0,
                    response.data.ciment_de_guinee.qtt ?? 0,
                ]);
            }
        } catch (error: any) {
            const message = error.response?.data?.message || "Erreur inconnue";
            toast.error(message);
        }
    }, []);

    useEffect(() => {
        if (data) {
            // Filtrage par texte
            const filteredByText = data.versements.filter((item) =>
                ["pv_num_paiement", "materiel", "pv_date_paiement", "four_name", "ets_name", "ets_code"].some((key) =>
                    item[key]?.toLowerCase().includes(filterText.toLowerCase())
                )
            );
            // Filtrage par mois
            const filterByMonths = filterByMonth(filteredByText, monthFilter);
            const finalFiltered = filterByEts(filterByMonths, etsFilter);

            setFilteredItems(finalFiltered ?? []);
        }
    }, [filterText, data, refresh, monthFilter, etsFilter]);

    // Initial data fetch
    useEffect(() => {
        fetchVersements();
    }, [fetchVersements, refresh]);

    const Confirmation = ({ id }: { id: number }) => {
            SweetAlert.fire({
                title: 'Êtes-vous sûr?',
                text: "De Vouloir Supprimer cet element ??",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Oui, Supprimer !'
            }).then((result) => {
                if (result.isConfirmed) {
                    deleteVersement({ id: id }, () => setRefresh(!refresh));
                }
            });
        };

    const cardValeur = ({ value }: { value: Verse[] }) => {
        let payed = 0;
        let payed_montant = 0;
        let attente = 0;
        let attente_montant = 0;
        let total = 0;
        let total_montant = 0;

        // Utilisation de `forEach` pour mettre à jour les compteurs
        value.forEach((item) => {
            if (item.status === 'Attente') {
                attente += 1;
                attente_montant += Number(item.pv_montant);
            } else if (item.status === 'Payé') {
                payed += 1;
                payed_montant += Number(item.pv_montant);
            }
            total += 1;
            total_montant += Number(item.montant);
        });
        return (
            <Row>
                <Col sm="12">
                    <Card className="progress-items pb-0 bg-light-info">
                        <CardHeader className="pb-4 bg-light-info">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <H4>
                                        <span className="text-dark fw-bold">{total.toLocaleString("gn-GN")}</span>
                                    </H4>
                                    <H6 className="f-20 f-w-600 mt-3">
                                        Total Achats
                                    </H6>
                                    <H3 className="f-15 font-light f-w-600 mt-3">
                                        Dont <span className="text-dark fw-bold">{formatPrice(total_montant)}</span>
                                    </H3>
                                </div>
                                <div>
                                    <i className="icofont icofont-bricks f-80"></i>
                                </div>
                            </div>
                        </CardHeader>
                    </Card>
                </Col>
                <Col md="6">
                    <Card className="progress-items pb-0 bg-light-warning">
                        <CardHeader className="pb-4 bg-light-warning">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <H4>
                                        <span className="text-dark fw-bold">{attente.toLocaleString("gn-GN")}</span>
                                    </H4>
                                    <H6 className="f-20 f-w-600 mt-3 text-white">
                                        Paiements En Attente
                                    </H6>
                                    <H3 className="f-15 text-white f-w-600 mt-3">
                                        Dont <span className="text-dark fw-bold">{formatPrice(attente_montant)}</span>
                                    </H3>
                                </div>
                                <div>
                                    <i className="icofont icofont-bank-transfer f-80"></i>
                                </div>
                            </div>
                        </CardHeader>
                    </Card>
                </Col>
                <Col md="6">
                    <Card className="progress-items pb-0 bg-light-success">
                        <CardHeader className="pb-4 bg-light-success">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <H4>
                                        <span className="text-dark fw-bold">{payed.toLocaleString("gn-GN")}</span>
                                    </H4>
                                    <H6 className="f-20 f-w-600 mt-3">
                                        Paiements Effectués
                                    </H6>
                                    <H3 className="f-15 font-light f-w-600 mt-3">
                                        Dont <span className="text-dark fw-bold">{formatPrice(payed_montant)}</span>
                                    </H3>
                                </div>
                                <div>
                                    <i className="icofont icofont-pay f-80"></i>
                                </div>
                            </div>
                        </CardHeader>
                    </Card>
                </Col>
            </Row>
        )
    };

    const isFiltered = filterText !== "" || monthFilter !== "" || etsFilter !== "";

    return (
        <div className='page-body'>
            <Breadcrumbs mainTitle="Versements" parent="Gestions" />
            <Container fluid>
                <Row>
                    <Col sm="4" md="4" >
                        <Card className="total-project">
                            <CardBody className="pt-0">
                                <Row>

                                    <Col xs="12">
                                        {chartData && <Chart chartData={chartData} />}

                                        <UL className="project-details simple-list">

                                            <LI className="d-flex align-items-center mt-3 justify-content-between p-0">
                                                <div className="d-flex align-items-center">
                                                    <div className={`circle-dot-primary`}>
                                                        <span />
                                                    </div>
                                                    <P className="ms-2 f-w-500">{data?.diamond.name ? data?.diamond.name : "Diamond de Guinée"}</P>
                                                </div>
                                                <span className="ms-1 f-w-500">{data?.diamond.qtt ? data?.diamond.qtt : 0}</span>
                                            </LI>
                                            <LI className="d-flex align-items-center mt-3 justify-content-between p-0">
                                                <div className="d-flex align-items-center">
                                                    <div className={`circle-dot-info`}>
                                                        <span />
                                                    </div>
                                                    <P className="ms-2 f-w-500">{data?.diamond_sougueta.name ? data?.diamond_sougueta.name : "Diamond Sougueta S.A"}</P>
                                                </div>
                                                <span className="ms-1 f-w-500">{data?.diamond_sougueta.qtt ? data?.diamond_sougueta.qtt : 0}</span>
                                            </LI>
                                            {/* <LI className="d-flex align-items-center mt-3 justify-content-between p-0">
                                                <div className="d-flex align-items-center">
                                                    <div className={`circle-dot-tertiary`}>
                                                        <span />
                                                    </div>
                                                    <P className="ms-2 f-w-500">{data?.cimaf.name ? data?.cimaf.name : "Cimaf"}</P>
                                                </div>
                                                <span className="ms-1 f-w-500">{data?.cimaf.qtt ? data?.cimaf.qtt : 0}</span>
                                            </LI> */}
                                            <LI className="d-flex align-items-center mt-3 justify-content-between p-0">
                                                <div className="d-flex align-items-center">
                                                    <div className={`circle-dot-dark`}>
                                                        <span />
                                                    </div>
                                                    <P className="ms-2 f-w-500">{data?.ciment_de_guinee.name ? data?.ciment_de_guinee.name : 'Ciment De Guinée'}</P>
                                                </div>
                                                <span className="ms-1 f-w-500">{data?.ciment_de_guinee.qtt ? data?.ciment_de_guinee.qtt : 0}</span>
                                            </LI>
                                        </UL>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col sm="8">
                        {cardValeur({ value: filteredItems })}
                    </Col>

                    <Col md="12">
                        <Card>
                            <CardBody>
                                <Row>
                                    <Col sm='9'>
                                        <Row>
                                            <Col sm="12" className="mb-2">
                                                <div className="dataTables_filter d-flex align-items-center">
                                                    <Input
                                                        type="search"
                                                        value={filterText}
                                                        placeholder="Recherche. . ."
                                                        onChange={(e) => setFilterText(e.target.value)}
                                                    />
                                                </div>
                                            </Col>
                                            <Col sm="6" className="mt-2">
                                                <Input
                                                    className="form-control"
                                                    type="select"
                                                    id="month"
                                                    value={monthFilter}
                                                    onChange={(e) => setMonthFilter(e.target.value)}
                                                >
                                                    <option value="">Sélectionner un mois</option>
                                                    {["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"].map((month) => (
                                                        <option key={month} value={month}>
                                                            {new Date(2024, parseInt(month) - 1).toLocaleString('default', { month: 'long' })}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </Col>
                                            <Col sm="6" className="mt-2">
                                                <FormGroup>
                                                    <Input
                                                        type="select"
                                                        className="form-control"
                                                        id="ets"
                                                        value={etsFilter}
                                                        onChange={(e) => setEtsFilter(e.target.value)}
                                                    >
                                                        <option value=''>Etablissement...</option>
                                                        {infosEts.map((item, index) => {
                                                            if (item.status === 'actif' && item.type === 'Personnel') {
                                                                return <option key={index} value={item.code}>{item.nom}</option>;
                                                            }
                                                            return null;
                                                        })}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col sm="3">
                                        {
                                            (itemHasPermission("super admin") || itemHasPermission('gest. versements ecriture')) && (
                                                <>
                                                    <Link to="/ajouter-un-versements" className="btn btn-light-dark for-light mb-2 w-100">
                                                        <i className="fa fa-plus"></i> Faire un versement
                                                    </Link>
                                                    <Link to="/ajouter-un-versements" className="btn btn-light-light for-dark mb-2 w-100">
                                                        <i className="fa fa-plus"></i> Faire un versement
                                                    </Link>
                                                </>
                                            )
                                        }
                                        <Btn color="danger" className="w-100 mt-2" onClick={clearFilters} disabled={!isFiltered}>
                                            <i className="fa fa-times"></i> Réinitialiser
                                        </Btn>
                                        
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                        <Table responsive id="all-invoice" striped={true}>
                            <thead className="bg-info" >
                                <tr>
                                    <th className="text-white">Id</th>
                                    <th className="text-white">Date & Fournisseur</th>
                                    <th className="text-white">ETABLISSEMENTS</th>
                                    <th className="text-white">Montant</th>
                                    <th className="text-white">Numéro Paiement</th>
                                    <th className="text-white">Date De Paiement</th>
                                    <th className="text-white">Stock Disponible</th>
                                    <th className="text-white">Actions</th>
                                </tr>
                            </thead>
                            <tbody>

                                {filteredItems.map((item, index) => (
                                    <tr key={item.id}>
                                        <th scope='row'>
                                            <span className="fw-bold">
                                                #{index + 1}
                                            </span>
                                        </th>
                                        <td>
                                            <span className="text-capitalize">
                                                {item.four_name}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="text-capitalize">
                                                {item.ets_name}
                                            </span>

                                        </td>
                                        <td>
                                            {
                                                item.status === 'Attente' ? (
                                                    <>
                                                        <span className="fw-bold text-warning text-capitalize">
                                                            {formatPrice(item.pv_montant)}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="fw-bold text-success text-capitalize">
                                                            {item.four_type === 'ciment_de_guinee' && item.status === 'Payé' ? formatPrice(item.mt_update) : formatPrice(item.montant)}
                                                        </span> 
                                                    </>
                                                )
                                            }

                                        </td>
                                        <td>
                                            <span className="fw-bold text-capitalize">
                                                {item.pv_num_paiement}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="fw-bold text-capitalize">
                                                {formatDate(item.pv_date_paiement)}
                                            </span>
                                        </td>
                                        <td>
                                            {
                                                item.qt_update > 0 && (item.four_type === 'diamond' || item.four_type === 'diamond_sougueta') ? (
                                                    <Badges color="light-success"> {item.qt_update} T</Badges>
                                                ) : item.montant > item.mt_update && item.four_type === 'cimaf' ? (
                                                    <Badges color="light-success"> {formatPrice(item.montant - item.mt_update)}</Badges>
                                                ) : item.qt_update > 0 && item.four_type === 'ciment_de_guinee' ? (
                                                    <Badges color="light-success">{item.qt_update} T  </Badges>
                                                ) : (
                                                    <Badges color="light-danger">Epuisé</Badges>

                                                )
                                            }
                                        </td>
                                        <td>
                                            <Link className='btn bg-dark btn-xs mx-1 my-1' to={`/show-versement/${item.id}`}>
                                                <i className='fa fa-eye'></i>
                                            </Link>
                                            
                                            {
                                                item.status === 'Attente' ? (
                                                    <Badges color="warning">{item.status}</Badges>
                                                ) : (
                                                    <Badges color="success">{item.status}</Badges>
                                                )
                                            }
                                            

                                            {
                                                (itemHasPermission('super admin') || itemHasPermission('gest. bons ecriture')) && (

                                                    <Btn className='bg-danger btn-xs mx-1 my-1' onClick={() => Confirmation({ id: item.id })}>
                                                        <i className='fa fa-trash'></i>
                                                    </Btn>

                                                )
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
        </div>

    )
}

export default GestVersementsV2